import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Notification, useLogin, useNotify } from 'react-admin'
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Divider,
  Typography,
} from '@mui/material'
import './Login.scss'
import { PublicClientApplication } from '@azure/msal-browser'
import MicrosoftLogo from '../../Components/Icons/MicrosoftLogo'
import { microsoftAuth } from '../../Utils/RestUtils'
import jwtDecode from 'jwt-decode'

const Login = ({}) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const login = useLogin()
  const notify = useNotify()
  const location = useLocation()
  const [referrer, setReferrer] = useState('')
  const msalInstance = new PublicClientApplication({
    auth: {
      clientId: process.env.REACT_APP_MICROSOFT_AUTH_CLIENT_ID,
      authority:
        'https://login.microsoftonline.com/' +
        process.env.REACT_APP_MICROSOFT_AUTH_TENANT_ID,
      redirectUri: process.env.REACT_APP_MICROSOFT_AUTH_REDIRECT_URI,
    },
  })

  useEffect(() => {
    const { nextPathname } = location.state || { nextPathname: referrer }
    setReferrer(nextPathname)
  }, [])

  const handleStandardLogin = async (e) => {
    e.preventDefault()
    setLoading(true)

    login({ email, password }, `${referrer}`)
      .then(() => {
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('Invalid email or password', { type: 'error' })
      })
  }

  const handleMicrosoftLogin = async () => {
    setLoading(true)
    try {
      await msalInstance.initialize()
      const response = await msalInstance.loginPopup({
        scopes: ['openid', 'profile', 'email'],
      })

      const idToken = response.idToken
      const result = await microsoftAuth(idToken)

      if (result.data?.token) {
        const decodedToken = jwtDecode(result.data.token)

        localStorage.setItem('token', result.data.token)
        localStorage.setItem('user_id', decodedToken.userID)
        localStorage.setItem('permissions', decodedToken.roles)
        localStorage.setItem('display_name', decodedToken.displayName)
        localStorage.setItem('email', decodedToken.username)
        localStorage.setItem('nice_active', decodedToken.niceActive)

        window.location.href = referrer || '/'
      } else {
        notify(result.data.error + ', ' + result.data.details, {
          type: 'error',
        })
      }
    } catch (error) {
      console.log(error)
      notify('Microsoft authentication failed', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid
      container
      spacing={2}
      justifyContent="center"
      alignItems="center"
      sx={{
        height: '100vh',
        marginTop: '0 !important',
        marginBottom: '0 !important',
      }}
    >
      <Card sx={{ maxWidth: '400px', paddingBottom: '18px' }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Login
          </Typography>

          <form onSubmit={handleStandardLogin}>
            <TextField
              type="email"
              name="email"
              label="Email"
              value={email}
              fullWidth
              margin="dense"
              size="small"
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              type="password"
              name="password"
              label="Password"
              value={password}
              fullWidth
              margin="dense"
              size="small"
              mb={2}
              onChange={(e) => setPassword(e.target.value)}
            />

            <Notification />
            <Button
              size="large"
              type="submit"
              disabled={loading}
              startIcon={
                loading && (
                  <CircularProgress size={14} thickness={3} color="primary" />
                )
              }
              sx={{ marginTop: '1rem' }}
              fullWidth
            >
              Login with Email
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          <Button
            size="large"
            onClick={handleMicrosoftLogin}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={14} thickness={3} color="primary" />
              ) : (
                <MicrosoftLogo size={20} />
              )
            }
            fullWidth
            variant="outlined"
            sx={{
              backgroundColor: '#fff',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            Login with Microsoft
          </Button>
        </CardContent>
      </Card>
    </Grid>
  )
}

export default Login
